import { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { FiPlus, FiEdit2, FiTrash2, FiX, FiSend, FiCheckCircle } from 'react-icons/fi';
import { useAuth } from '../contexts/AuthContext';
import api from '../api';

const NOTICE_TYPES = {
  holiday: '휴원 안내',
  briefing: '설명회 안내',
  special: '특강 안내',
  schedule: '수업일정 안내',
  exam: '시험 안내',
  consult: '상담 안내'
};

export default function ParentNotice() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [notices, setNotices] = useState([]);
  const [students, setStudents] = useState([]);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedNotice, setSelectedNotice] = useState(null);
  const [editingNotice, setEditingNotice] = useState(null);
  const [targetType, setTargetType] = useState('all'); // all, class, students
  const [formData, setFormData] = useState({
    type: 'holiday',
    title: '',
    content: '',
    classGroupId: '',
    targetStudents: []
  });

  useEffect(() => {
    loadData();
    if (isAdmin) {
      loadStudents();
      loadClasses();
    }
  }, [isAdmin]);

  const loadData = async () => {
    try {
      const res = await api.get('/api/parent-notice');
      setNotices(res.data.notices || []);
    } catch (error) {
      console.error('Failed to load notices:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadStudents = async () => {
    try {
      const res = await api.get('/api/users?role=student&isActive=true');
      setStudents(res.data.users || []);
    } catch (error) {
      console.error('Failed to load students:', error);
    }
  };

  const loadClasses = async () => {
    try {
      const res = await api.get('/api/class-groups?active=true');
      setClasses(res.data.classGroups || []);
    } catch (error) {
      console.error('Failed to load classes:', error);
    }
  };

  const openCreateModal = () => {
    setEditingNotice(null);
    setTargetType('all');
    setFormData({ type: 'holiday', title: '', content: '', classGroupId: '', targetStudents: [] });
    setShowModal(true);
  };

  const openEditModal = (notice) => {
    if (notice.notificationSent) {
      alert('이미 발송된 공지는 수정할 수 없습니다');
      return;
    }
    setEditingNotice(notice);

    // targetType 결정
    if (notice.classGroupId) {
      setTargetType('class');
    } else if (notice.targetStudents?.length > 0) {
      setTargetType('students');
    } else {
      setTargetType('all');
    }

    setFormData({
      type: notice.type,
      title: notice.title,
      content: notice.content,
      classGroupId: notice.classGroupId || '',
      targetStudents: notice.targetStudents?.map(s => s._id || s) || []
    });
    setShowModal(true);
  };

  const openDetail = (notice) => {
    setSelectedNotice(notice);
    setShowDetailModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title || !formData.content) {
      alert('제목과 내용을 입력해주세요');
      return;
    }

    // 대상에 따라 데이터 구성
    let submitData = {
      type: formData.type,
      title: formData.title,
      content: formData.content
    };

    if (targetType === 'all') {
      // 전체 발송: targetStudents 비우기
      submitData.targetStudents = [];
      submitData.classGroupId = null;
    } else if (targetType === 'class') {
      // 반별 발송: 선택한 반의 모든 학생 ID를 targetStudents에 담기
      if (!formData.classGroupId) {
        alert('반을 선택해주세요');
        return;
      }

      const selectedClass = classes.find(c => c._id === formData.classGroupId);
      if (!selectedClass || !selectedClass.students || selectedClass.students.length === 0) {
        alert('선택한 반에 학생이 없습니다');
        return;
      }

      // 학생 ID 추출 (객체일 수도 있고 ID 문자열일 수도 있음)
      const studentIds = selectedClass.students.map(s => s._id || s);
      submitData.targetStudents = studentIds;
      submitData.classGroupId = formData.classGroupId;
    } else if (targetType === 'students') {
      // 개별 학생 발송
      if (formData.targetStudents.length === 0) {
        alert('최소 1명 이상의 학생을 선택해주세요');
        return;
      }
      submitData.targetStudents = formData.targetStudents;
      submitData.classGroupId = null;
    }

    try {
      if (editingNotice) {
        await api.put(`/api/parent-notice/${editingNotice._id}`, submitData);
        alert('수정되었습니다');
      } else {
        await api.post('/api/parent-notice', submitData);
        alert('공지가 등록되었습니다');
      }
      setShowModal(false);
      loadData();
    } catch (error) {
      alert(error.response?.data?.error || '처리에 실패했습니다');
    }
  };

  const handleDelete = async (notice) => {
    if (!window.confirm('이 공지를 삭제하시겠습니까?')) return;

    try {
      await api.delete(`/api/parent-notice/${notice._id}`);
      alert('삭제되었습니다');
      loadData();
    } catch (error) {
      alert(error.response?.data?.error || '삭제에 실패했습니다');
    }
  };

  const handleSend = async (notice) => {
    const target = notice.classGroupId
      ? `${notice.classGroupId.name || notice.classGroupId} (${notice.targetStudents?.length || 0}명)`
      : notice.targetStudents?.length > 0
      ? `${notice.targetStudents.length}명의 학생 학부모`
      : '전체 학부모';

    if (!window.confirm(`${target}에게 알림을 발송하시겠습니까?`)) return;

    try {
      const res = await api.post(`/api/parent-notice/${notice._id}/send`);
      const result = res.data.result;
      alert(`알림 발송 완료\n성공: ${result.sent}명 / 실패: ${result.failed}명\n사용 포인트: ${result.pointsUsed}P`);
      loadData();
    } catch (error) {
      alert(error.response?.data?.error || '발송에 실패했습니다');
    }
  };

  const stats = {
    total: notices.length,
    sent: notices.filter(n => n.notificationSent).length,
    pending: notices.filter(n => !n.notificationSent).length
  };

  return (
    <div className="parent-notice-page">
      <div className="page-header">
        <h2>학부모 공지</h2>
        {isAdmin && (
          <button className="btn btn-primary" onClick={openCreateModal}>
            <FiPlus /> 공지 작성
          </button>
        )}
      </div>

      {/* Stats */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-value">{stats.total}</div>
          <div className="stat-label">전체 공지</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{stats.sent}</div>
          <div className="stat-label">발송 완료</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{stats.pending}</div>
          <div className="stat-label">대기 중</div>
        </div>
      </div>

      {/* Notices List */}
      <div className="notice-list">
        {loading ? (
          <div className="loading">로딩 중...</div>
        ) : notices.length === 0 ? (
          <div className="empty">공지사항이 없습니다</div>
        ) : (
          notices.map(notice => (
            <div key={notice._id} className={`notice-item ${notice.notificationSent ? 'sent' : ''}`}>
              <div className="notice-header">
                <span className="notice-type">{NOTICE_TYPES[notice.type]}</span>
                {notice.notificationSent && (
                  <span className="badge badge-success">
                    <FiCheckCircle /> 발송 완료
                  </span>
                )}
              </div>
              <div className="notice-content" onClick={() => openDetail(notice)}>
                <h3>{notice.title}</h3>
                <p>{notice.content.substring(0, 100)}{notice.content.length > 100 ? '...' : ''}</p>
                <div className="notice-meta">
                  <span>{format(new Date(notice.createdAt), 'yyyy-MM-dd HH:mm')}</span>
                  <span>
                    {notice.classGroupId
                      ? `대상: ${notice.classGroupId.name || notice.classGroupId} (${notice.targetStudents?.length || 0}명)`
                      : notice.targetStudents?.length > 0
                      ? `대상: ${notice.targetStudents.length}명`
                      : '대상: 전체'}
                  </span>
                  {notice.notificationSent && (
                    <span>발송: {format(new Date(notice.notificationSentAt), 'yyyy-MM-dd HH:mm')}</span>
                  )}
                </div>
              </div>
              {isAdmin && (
                <div className="notice-actions">
                  {!notice.notificationSent && (
                    <>
                      <button
                        className="btn btn-success btn-sm"
                        onClick={() => handleSend(notice)}
                        title="알림 발송"
                      >
                        <FiSend /> 발송
                      </button>
                      <button
                        className="btn btn-secondary btn-sm"
                        onClick={() => openEditModal(notice)}
                      >
                        <FiEdit2 /> 수정
                      </button>
                    </>
                  )}
                  <button
                    className="btn btn-danger btn-sm"
                    onClick={() => handleDelete(notice)}
                  >
                    <FiTrash2 /> 삭제
                  </button>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingNotice ? '공지 수정' : '공지 작성'}</h3>
              <button className="close-btn" onClick={() => setShowModal(false)}>
                <FiX />
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>공지 유형 *</label>
                <select
                  value={formData.type}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                  required
                >
                  {Object.entries(NOTICE_TYPES).map(([key, label]) => (
                    <option key={key} value={key}>{label}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>제목 *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="공지 제목을 입력하세요"
                  required
                />
              </div>

              <div className="form-group">
                <label>내용 *</label>
                <textarea
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  placeholder="공지 내용을 입력하세요"
                  rows="8"
                  required
                  style={{ minHeight: '150px' }}
                />
              </div>

              <div className="form-group">
                <label>발송 대상 *</label>
                <select
                  value={targetType}
                  onChange={(e) => {
                    setTargetType(e.target.value);
                    setFormData({ ...formData, classGroupId: '', targetStudents: [] });
                  }}
                >
                  <option value="all">전체 학부모</option>
                  <option value="class">반별 선택</option>
                  <option value="students">개별 학생 선택</option>
                </select>
              </div>

              {targetType === 'class' && (
                <div className="form-group">
                  <label>반 선택 *</label>
                  <select
                    value={formData.classGroupId}
                    onChange={(e) => setFormData({ ...formData, classGroupId: e.target.value })}
                    required
                  >
                    <option value="">선택하세요</option>
                    {classes.filter(c => c.students?.length > 0).map((c) => (
                      <option key={c._id} value={c._id}>
                        {c.name} ({c.students?.length || 0}명)
                      </option>
                    ))}
                  </select>
                  {classes.every(c => !c.students?.length) && (
                    <small className="text-muted">등록된 학생이 있는 반이 없습니다</small>
                  )}
                </div>
              )}

              {targetType === 'students' && (
                <div className="form-group">
                  <label>학생 선택 * ({formData.targetStudents.length}명 선택됨)</label>
                  <div className="students-checkboxes" style={{
                    maxHeight: '200px',
                    overflowY: 'auto',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    padding: '8px'
                  }}>
                    {students.map(student => (
                      <label key={student._id} style={{ display: 'block', marginBottom: '6px' }}>
                        <input
                          type="checkbox"
                          checked={formData.targetStudents.includes(student._id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setFormData({
                                ...formData,
                                targetStudents: [...formData.targetStudents, student._id]
                              });
                            } else {
                              setFormData({
                                ...formData,
                                targetStudents: formData.targetStudents.filter(id => id !== student._id)
                              });
                            }
                          }}
                        />
                        {' '}{student.name}
                      </label>
                    ))}
                  </div>
                </div>
              )}

              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                  취소
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingNotice ? '수정' : '등록'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {showDetailModal && selectedNotice && (
        <div className="modal-overlay" onClick={() => setShowDetailModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <span className="notice-type">{NOTICE_TYPES[selectedNotice.type]}</span>
                <h3>{selectedNotice.title}</h3>
              </div>
              <button className="close-btn" onClick={() => setShowDetailModal(false)}>
                <FiX />
              </button>
            </div>
            <div className="modal-body">
              <div className="notice-detail-content">
                <p style={{ whiteSpace: 'pre-wrap' }}>{selectedNotice.content}</p>
              </div>
              <div className="notice-detail-meta">
                <p>작성일: {format(new Date(selectedNotice.createdAt), 'yyyy-MM-dd HH:mm')}</p>
                <p>대상: {selectedNotice.classGroupId
                  ? `${selectedNotice.classGroupId.name || selectedNotice.classGroupId} (${selectedNotice.targetStudents?.length || 0}명)`
                  : selectedNotice.targetStudents?.length > 0
                  ? `${selectedNotice.targetStudents.length}명의 학생 학부모`
                  : '전체 학부모'}</p>
                {selectedNotice.notificationSent && (
                  <p>발송일: {format(new Date(selectedNotice.notificationSentAt), 'yyyy-MM-dd HH:mm')}</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
